import { useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import {
  cancelMovement,
  openMovementModal,
  resetMovementNodes,
  selectMovementIntent,
  selectMovementTroops,
  selectSelectedNodes,
} from "src/store/ui"
import { selectGameConfig } from "src/store/common"
import {
  selectCurrentPlayer,
  selectIsCurrentPlayerReady,
  selectPlayerFood,
  selectResearchLevels,
} from "src/store/game"
import { cellDisplayName, expandNodesToPath } from "src/utils/maps"
import { etaForPath, tripCostForPath } from "src/utils/movementEta"
import { foodIcon } from "src/utils/resources"
import { consumptionMultiplier } from "src/utils/gameModifiers"
import UnitItem from "src/components/UnitItem"
import { actionForKey, isPlainKeyPress } from "src/utils/shortcuts"
import useShortcuts from "src/hooks/useShortcuts"
import ModalService from "src/modals"

import "./RoutePlanner.css"

/* The route half of a march, in the action bar's place. The force is picked FIRST (owner's Q2),
   so as the path is drawn this bar can say the thing the player actually plans around: how many
   rounds the trip takes, and what provisioning it costs — each unit's upkeep per round of its own
   travel (Q13c, owner 2026-10-03), the same arithmetic the server bills at departure. The march is
   sent from here; the modal only reopens to change the force. */
const RoutePlanner = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const nodes = useSelector(selectSelectedNodes)
  const intent = useSelector(selectMovementIntent)
  const troops = useSelector(selectMovementTroops)
  const gameConfig = useSelector(selectGameConfig)
  const player = useSelector(selectCurrentPlayer)
  const researchLevels = useSelector(selectResearchLevels)
  const isReady = useSelector(selectIsCurrentPlayerReady)
  const food = useSelector(selectPlayerFood)
  const shortcuts = useShortcuts()

  const team = player?.team
  const race = player?.race
  const troopConfig = gameConfig?.[team === "human" ? "human_config" : "zombie_config"]?.TROOPS || {}

  const origin = nodes.length ? nodes[nodes.length - 1] : null
  const path = expandNodesToPath(nodes)
  const maxPath = gameConfig?.rules_config?.MAX_PATH_LENGTH ?? Infinity
  const tooLong = path.length > maxPath
  // A colony expedition is a march whose force was picked for it (owner, 2026-09-07); it is sent
  // as one, and only the wording here says what it is for — and that Settle is still to come.
  const isColonization = intent === "colonize"

  const chosen = Object.entries(troops || {}).filter(([, number]) => Number(number) > 0)
  const totalUnits = chosen.reduce((total, [, number]) => total + Number(number), 0)
  const eta = etaForPath({ gameConfig, team, troops, path, race, researchLevels })
  // The trip charge, exactly as move_troops bills it: each unit's upkeep once per round of its
  // OWN march (owner, 2026-10-03), so a slow tank does not make the soldiers beside it pay more.
  const tripCost = eta != null ? tripCostForPath({ gameConfig, team, troops, path, race, researchLevels }) : null
  // One round of the whole stack's upkeep: a leg of N of the stack's rounds costs at most N of these,
  // because no unit needs more rounds than the stack, so it is a safe measure of an affordable leg.
  const upkeepPerRound = Math.trunc(
    chosen.reduce((total, [unit, number]) => total + (troopConfig[unit]?.CONSUMPTION || 0) * Number(number), 0) *
      consumptionMultiplier({ gameConfig, race })
  )

  /* A march is provisioned in full at departure, so a long road is refused by the bank rather
     than by the map (`InsufficientFoodForTrip`). What that refusal never said is the way round
     it, which is the way the bots cross the same distances: order one leg, and re-order from
     where the stack stands next round — each leg provisions its own rounds, and the income
     between them pays for the next. So when the bank is short this says how long a leg it can
     afford, and the player walks the road in those. The button stays live: the server is the
     gate, and this arithmetic is a mirror of it, not a second copy of the rule. */
  const cantProvision = tripCost != null && tripCost > food
  const affordableRounds = upkeepPerRound > 0 ? Math.floor(food / upkeepPerRound) : 0
  const provisionWarning = !cantProvision
    ? null
    : affordableRounds >= 1
      ? t("game.route.cantProvision", { have: food, count: affordableRounds })
      : t("game.route.cantProvisionNothing", { have: food })

  const ready = nodes.length >= 2 && !tooLong && totalUnits > 0 && !isReady

  const send = () => {
    if (!ready) return
    const sent = wsClient.send("game", {
      action: "move_troops",
      path,
      troops: Object.fromEntries(chosen),
    })
    // A dropped socket keeps the draft: the player can try again when it is back.
    if (sent === false) return
    dispatch(cancelMovement())
  }

  // Keyboard, matching the map bar's contract: MARCHAR pressed again restarts the route
  // (owner's Q4), Space sends the march once it is ready (owner's P13).
  const latest = useRef({})
  latest.current = { ready, send, canReset: nodes.length >= 2 }
  useEffect(() => {
    const onKeyDown = (event) => {
      if (ModalService.hasOpenModals()) return
      if (event.key === " ") {
        if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return
        if (!latest.current.ready) return
        event.preventDefault()
        latest.current.send()
        return
      }
      if (!isPlainKeyPress(event)) return
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return
      const action = actionForKey(shortcuts, event.key)
      // MARCHAR again, or the reset key (R by default, owner 2026-10-26): back to the origin.
      if (action === "march" || (action === "resetRoute" && latest.current.canReset)) {
        event.preventDefault()
        dispatch(resetMovementNodes())
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [shortcuts, dispatch])

  if (!origin) return null

  const originName = cellDisplayName({ row: origin.row, column: origin.col, held: true })

  /* Only the refusal the server would give is written out (owner, 2026-09-29): the drawing hint
     and the cell count went, and the provisions are a tag beside the rounds — green while the bank
     pays for them, red with the way round in its title when it does not. */
  const status = tooLong ? t("game.route.tooLong", { max: maxPath }) : null

  return (
    <div className={`route-planner${isColonization ? " colonize" : ""}`} role="toolbar">
      <div className="route-planner-target">
        <p className="route-planner-title">
          <span className="route-planner-icon">{isColonization ? "🧭" : "⚔"}</span>
          {isColonization ? t("game.route.colonizeFrom") : t("game.route.marchFrom")}{" "}
          <strong>{originName}</strong> <span className="route-planner-coords">{origin.col}-{origin.row}</span>
          {isColonization ? ` · ${t("game.route.settleOnArrival", { key: (shortcuts.settle || "").toUpperCase() })}` : ""}
        </p>
        {/* The force as the picker left it — the side bar's own plate, count inside the picture
            (owner, 2026-09-29) — then the rounds the road takes and what it prepays. */}
        <div className="route-planner-force" aria-label={t("game.route.forceSummary", { count: totalUnits })}>
          {chosen.map(([unit, number]) => (
            <span className="route-planner-unit" key={unit} title={t(`city.units.${unit}`, { defaultValue: unit })}>
              <UnitItem type={unit} count={Number(number)} />
            </span>
          ))}
          {nodes.length >= 2 && (
            <span className="route-planner-tags">
              {eta != null && (
                <span className="route-planner-eta" data-testid="route-eta">
                  {t("game.route.eta", { count: eta })}
                </span>
              )}
              {tripCost != null && tripCost > 0 && (
                <span
                  className={`route-planner-food${cantProvision ? " short" : ""}`}
                  data-testid="route-food"
                  title={provisionWarning || undefined}
                >
                  {foodIcon(team)} {tripCost}
                </span>
              )}
            </span>
          )}
        </div>
        {status && <p className={`route-planner-status${tooLong || cantProvision ? " blocked" : ""}`}>{status}</p>}
      </div>
      <div className="route-planner-buttons">
        <button type="button" className="quiet" data-action="choose-troops" onClick={() => dispatch(openMovementModal())}>
          {t("game.route.editForce")}
        </button>
        <button type="button" className="quiet" onClick={() => dispatch(resetMovementNodes())} disabled={nodes.length < 2}>
          {t("game.route.reset")}
          {shortcuts.resetRoute && <kbd className="map-action-key">{shortcuts.resetRoute.toUpperCase()}</kbd>}
        </button>
        <button type="button" className="cancel" onClick={() => dispatch(cancelMovement())}>
          {t("game.route.cancel")}
        </button>
        <button type="button" className="route-planner-next" data-action="send-route" onClick={send} disabled={!ready}>
          {t("game.route.send", { count: totalUnits })}
        </button>
      </div>
    </div>
  )
}

export default RoutePlanner
